/**
 * Le problème à N corps — le moteur du bac à sable.
 *
 * Les trois expériences calculaient une trajectoire à l'avance, pour un corps,
 * autour d'un centre immobile. Ici tout le monde attire tout le monde, tout le
 * monde bouge, et l'utilisateur modifie le monde pendant qu'il tourne. Il n'y a
 * plus de chemin à précalculer : le monde avance, pas à pas.
 *
 * ## Pourquoi ce module refait un pas de Verlet plutôt que d'appeler l'autre
 *
 * `integrator.step` intègre **un** corps dans un champ donné. Ici les
 * accélérations dépendent des positions de tous les autres, et velocity Verlet
 * exige que la seconde évaluation se fasse **après** que tout le monde a bougé.
 * L'appliquer corps par corps avec un champ figé donnerait un autre schéma —
 * qui aurait l'air de marcher, et perdrait la propriété qu'on est venu
 * chercher. Le schéma est donc réécrit ici, à l'identique, pour un système
 * couplé ; c'est la même mécanique, appliquée d'un coup.
 *
 * ## Ce qui remplace la référence analytique
 *
 * À trois corps et au-delà, aucune forme close n'existe : rien à quoi comparer.
 * Ce sont les **invariants** qui tiennent lieu de référence, et ils sont
 * exacts — quantité de mouvement conservée, centre de masse en ligne droite,
 * énergie bornée. Les bancs sont écrits contre eux.
 *
 * ## Ce que ce module ne fait pas
 *
 * Il ne rend pas d'énergie ni de quantité de mouvement en unités physiques :
 * il rend leurs **dérives relatives**, sans dimension par construction. C'est
 * ce que le banc vérifie et ce que la vue affiche. Le jour où une expérience
 * réclamera des joules, la dimension sera ajoutée au vocabulaire — pas avant
 * (CLAUDE.md §1 ; on n'écrit pas de code « au cas où »).
 */

import { coreFr } from "../i18n/core-fr.js";
import { abs, max, sqrt } from "./math.js";
import type { Vector2 } from "./integrator.js";
import type { Quantity } from "./units.js";
import { UnitError, dimensionless, quantity, valueIn } from "./units.js";

/**
 * Constante de gravitation, en m³·kg⁻¹·s⁻².
 *
 * **Référence externe** (CODATA), que ce projet n'a pas mesurée.
 */
const G = 6.6743e-11;

/**
 * Adoucissement, en mètres.
 *
 * Deux points matériels au même endroit donnent une accélération infinie, ce
 * qui n'est pas une physique mais une division par zéro. On remplace `r²` par
 * `r² + ε²` : loin, cela ne change rien de mesurable ; près, cela borne. La
 * valeur vaut mille kilomètres — sous cette distance, deux corps célestes se
 * seraient de toute façon percutés, et ce moteur ne modélise pas les contacts.
 */
const SOFTENING = 1e6;

export interface WorldBody {
  readonly id: string;
  readonly label: string;
  readonly mass: Quantity<"mass">;
  readonly position: Vector2<"length">;
  readonly velocity: Vector2<"velocity">;
}

/**
 * Une paire de nombres nus, **privée** au module.
 *
 * La quantité de mouvement n'a pas d'unité dans le vocabulaire, et l'ajouter
 * pour la seule commodité d'un banc reviendrait à écrire du code « au cas où ».
 * Elle ne sort donc pas d'ici : ce qui sort, ce sont des grandeurs dimensionnées
 * (le barycentre) ou des rapports sans dimension (les dérives, l'équilibre).
 */
interface Pair {
  readonly x: number;
  readonly y: number;
}

// ── Le pas ───────────────────────────────────────────────────────────────────

interface Raw {
  readonly mass: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

function rawOf(bodies: readonly WorldBody[]): Raw[] {
  return bodies.map((one) => ({
    mass: valueIn(one.mass, "kg"),
    x: valueIn(one.position.x, "m"),
    y: valueIn(one.position.y, "m"),
    vx: valueIn(one.velocity.x, "m/s"),
    vy: valueIn(one.velocity.y, "m/s"),
  }));
}

/**
 * Accélérations mutuelles, calculées **par paires**.
 *
 * Chaque paire n'est traitée qu'une fois, et sa contribution est ajoutée aux
 * deux corps avec des signes opposés. Ce n'est pas seulement deux fois moins de
 * calcul : c'est ce qui rend la conservation de la quantité de mouvement exacte
 * à l'arrondi près, parce que ce qu'un corps gagne est littéralement le nombre
 * que l'autre perd.
 */
function accelerations(bodies: readonly Raw[]): { ax: number[]; ay: number[] } {
  const ax = bodies.map(() => 0);
  const ay = bodies.map(() => 0);

  for (let i = 0; i < bodies.length; i += 1) {
    for (let j = i + 1; j < bodies.length; j += 1) {
      const a = bodies[i]!;
      const b = bodies[j]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const squared = dx * dx + dy * dy + SOFTENING * SOFTENING;
      const distance = sqrt(squared);
      const inverseCube = 1 / (squared * distance);

      ax[i] = ax[i]! + G * b.mass * dx * inverseCube;
      ay[i] = ay[i]! + G * b.mass * dy * inverseCube;
      ax[j] = ax[j]! - G * a.mass * dx * inverseCube;
      ay[j] = ay[j]! - G * a.mass * dy * inverseCube;
    }
  }

  return { ax, ay };
}

/**
 * Un pas de velocity Verlet pour le système entier.
 *
 * ```
 * v½ = v₀ + ½·a₀·dt
 * x₁ = x₀ + v½·dt        ← tout le monde bouge avant la seconde évaluation
 * v₁ = v½ + ½·a₁·dt
 * ```
 *
 * Ne modifie rien : les mondes sont immuables, et deux simulations qui
 * partagent un état ne peuvent pas se marcher dessus.
 */
export function nbodyStep<T extends WorldBody>(
  bodies: readonly T[],
  duration: Quantity<"time">,
): readonly T[] {
  const dt = valueIn(duration, "s");
  if (!(dt > 0)) {
    throw new UnitError(
      coreFr.integrator.invalidStep(String(dt)),
    );
  }

  const raw = rawOf(bodies);
  const first = accelerations(raw);

  for (let index = 0; index < raw.length; index += 1) {
    const one = raw[index]!;
    one.vx += 0.5 * first.ax[index]! * dt;
    one.vy += 0.5 * first.ay[index]! * dt;
    one.x += one.vx * dt;
    one.y += one.vy * dt;
  }

  const second = accelerations(raw);

  return bodies.map((one, index) => {
    const moved = raw[index]!;
    return {
      ...one,
      position: { x: quantity(moved.x, "m"), y: quantity(moved.y, "m") },
      velocity: {
        x: quantity(moved.vx + 0.5 * second.ax[index]! * dt, "m/s"),
        y: quantity(moved.vy + 0.5 * second.ay[index]! * dt, "m/s"),
      },
    };
  });
}

/**
 * `steps` pas successifs.
 *
 * Le nombre de pas est une **entrée explicite**, jamais une durée d'horloge :
 * un monde avancé de mille pas est le même sur toutes les machines, quelle que
 * soit la vitesse à laquelle elles les ont exécutés (CLAUDE.md §5).
 */
export function advance<T extends WorldBody>(
  bodies: readonly T[],
  duration: Quantity<"time">,
  steps: Quantity<"dimensionless">,
): readonly T[] {
  const count = valueIn(steps, "1");
  if (!Number.isInteger(count) || count < 0) {
    throw new UnitError(coreFr.integrator.invalidStepCount(String(count)));
  }

  let current: readonly T[] = bodies;
  for (let index = 0; index < count; index += 1) {
    current = nbodyStep(current, duration);
  }
  return current;
}

// ── Les invariants ───────────────────────────────────────────────────────────

export function totalMass(bodies: readonly WorldBody[]): Quantity<"mass"> {
  let total = 0;
  for (const one of bodies) total += valueIn(one.mass, "kg");
  return quantity(total, "kg");
}

/** Quantité de mouvement totale, en kg·m/s. Aucune force intérieure ne la change. */
function totalMomentum(bodies: readonly WorldBody[]): Pair {
  let x = 0;
  let y = 0;
  for (const one of bodies) {
    const mass = valueIn(one.mass, "kg");
    x += mass * valueIn(one.velocity.x, "m/s");
    y += mass * valueIn(one.velocity.y, "m/s");
  }
  return { x, y };
}

/** Centre de masse. Il avance en ligne droite, à vitesse constante. */
export function centreOfMass(bodies: readonly WorldBody[]): Vector2<"length"> {
  const mass = valueIn(totalMass(bodies), "kg");
  if (mass <= 0) return { x: quantity(0, "m"), y: quantity(0, "m") };

  let x = 0;
  let y = 0;
  for (const one of bodies) {
    const share = valueIn(one.mass, "kg");
    x += share * valueIn(one.position.x, "m");
    y += share * valueIn(one.position.y, "m");
  }
  return { x: quantity(x / mass, "m"), y: quantity(y / mass, "m") };
}

/**
 * Part des quantités de mouvement individuelles qui ne se compensent pas.
 *
 * Sans dimension par construction : c'est la somme vectorielle rapportée à la
 * somme des normes. Elle vaut zéro pour un système monté symétriquement, et le
 * dire ainsi évite d'inventer une unité de quantité de mouvement pour le seul
 * besoin d'un banc.
 */
export function momentumBalance(bodies: readonly WorldBody[]): Quantity<"dimensionless"> {
  const total = totalMomentum(bodies);
  return dimensionless(
    sqrt(total.x * total.x + total.y * total.y) / max(scaleOf(bodies), 1e-30),
  );
}

/** Somme des normes des quantités de mouvement : l'échelle à laquelle rapporter. */
function scaleOf(bodies: readonly WorldBody[]): number {
  let scale = 0;
  for (const one of bodies) {
    const mass = valueIn(one.mass, "kg");
    scale += mass * sqrt(valueIn(one.velocity.x, "m/s") ** 2 + valueIn(one.velocity.y, "m/s") ** 2);
  }
  return scale;
}

/** Énergie totale, cinétique plus potentielle, en joules. Usage interne. */
function energyOf(bodies: readonly WorldBody[]): number {
  const raw = rawOf(bodies);

  let kinetic = 0;
  for (const one of raw) {
    kinetic += 0.5 * one.mass * (one.vx * one.vx + one.vy * one.vy);
  }

  let potential = 0;
  for (let i = 0; i < raw.length; i += 1) {
    for (let j = i + 1; j < raw.length; j += 1) {
      const a = raw[i]!;
      const b = raw[j]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distance = sqrt(dx * dx + dy * dy + SOFTENING * SOFTENING);
      potential -= (G * a.mass * b.mass) / distance;
    }
  }

  return kinetic + potential;
}

/**
 * Dérive relative de l'énergie entre deux états du même monde.
 *
 * Sans dimension par construction : c'est un écart rapporté à une énergie, et
 * c'est exactement ce qui se vérifie et s'affiche. Un schéma symplectique la
 * fait osciller autour de zéro ; un schéma ordinaire la fait croître.
 */
export function energyDrift(
  before: readonly WorldBody[],
  after: readonly WorldBody[],
): Quantity<"dimensionless"> {
  const start = energyOf(before);
  const end = energyOf(after);
  const scale = max(abs(start), 1e-30);
  return dimensionless((end - start) / scale);
}

/** Dérive relative de la quantité de mouvement. Elle doit rester au niveau du bruit. */
export function momentumDrift(
  before: readonly WorldBody[],
  after: readonly WorldBody[],
): Quantity<"dimensionless"> {
  const start = totalMomentum(before);
  const end = totalMomentum(after);

  const gap = sqrt((end.x - start.x) ** 2 + (end.y - start.y) ** 2);
  // Rapportée à l'échelle des quantités de mouvement en présence, pas à leur
  // somme : un système symétrique a une somme nulle, et diviser par zéro
  // transformerait un invariant parfait en infini.
  return dimensionless(gap / max(scaleOf(after), 1e-30));
}
